import { useEffect, useCallback } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { toast } from "@/components/ui/sonner";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { ROUTES } from '@/lib/routes';
import { useReducerWithDevtools } from '@/lib/useReducerWithDevtools';
import { searchReducer, initialSearchState } from '@/reducers/searchReducer';

export const useSearchState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [state, dispatch] = useReducerWithDevtools(
    searchReducer,
    initialSearchState,
    'Search'
  );

  // Initialize query from URL params
  useEffect(() => {
    const queryParam = searchParams.get('q');
    if (queryParam) {
      dispatch({ type: 'SET_QUERY', payload: queryParam });
    }
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate(ROUTES.AUTH);
    }
  }, [user, authLoading, navigate]);

  // Perform search when URL params change
  useEffect(() => {
    const searchQuery = searchParams.get('q');
    if (searchQuery && user) {
      performSearch(searchQuery);
    }
  }, [searchParams, user]);

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Perform a simple text search across messages
      const { data, error } = await supabase
        .from('messages')
        .select(`
          id,
          content,
          session_id,
          created_at,
          sessions!inner (
            name,
            projects!inner (
              boards!inner (
                workspaces!inner (
                  user_id
                )
              )
            )
          )
        `)
        .ilike('content', `%${searchQuery}%`)
        .limit(50);

      if (error) throw error;

      // Transform results
      const transformedResults = data.map((item: any) => ({
        id: item.id,
        content: item.content,
        session_id: item.session_id,
        session_name: item.sessions.name,
        created_at: item.created_at,
      }));

      dispatch({ type: 'SET_RESULTS', payload: transformedResults });
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
      dispatch({ type: 'SET_RESULTS', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const handleSearch = useCallback(() => {
    if (state.query.trim()) {
      setSearchParams({ q: state.query });
    }
  }, [state.query, setSearchParams]);

  const handleQueryChange = useCallback((newQuery: string) => {
    dispatch({ type: 'SET_QUERY', payload: newQuery });
  }, []);

  const clearResults = useCallback(() => {
    dispatch({ type: 'CLEAR_RESULTS' });
    setSearchParams({});
  }, [setSearchParams]);

  const navigateToSession = useCallback((sessionId: string) => {
    navigate(`/sessions/${sessionId}`);
  }, [navigate]);

  return {
    state,
    dispatch,
    handleSearch,
    handleQueryChange,
    clearResults,
    navigateToSession,
    searchParams,
  };
};
